import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function EventsAdminSection() {
  const [events, setEvents] = useState<any[]>([]);
  const [tutors, setTutors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: '', tutor: '', date: '', time: '', duration: '', spots: '', category: '', customCategory: '' });
  const [toasts, setToasts] = useState<any[]>([]);
  const addToast = (message: string, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState<{ id: string; message: string } | null>(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewPayload, setViewPayload] = useState<any | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';
  const navigate = useNavigate();
  const location = useLocation();
  const skipOpenRef = useRef(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  // helper: format date as dd/mm/yyyy
  const formatDate = (d: string | undefined) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('en-GB');
    } catch (e) {
      return d;
    }
  };

  // helper: format time with am/pm
  const formatTime = (t: string | undefined) => {
    if (!t) return '-';
    try {
      // ensure seconds exist
      const timeStr = t.length === 5 ? `${t}:00` : t;
      const dt = new Date(`1970-01-01T${timeStr}`);
      return dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch (e) {
      return t;
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const evRes = await fetch(`${API_BASE}/api/events`);
      if (evRes.ok) {
        const evData = await evRes.json();
        setEvents(evData);
      }
      const tutRes = await fetch(`${API_BASE}/api/users?role=tutor`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (tutRes.ok) {
        const tutData = await tutRes.json();
        setTutors(tutData);
      }
    } catch (error) {
      console.error('Failed to fetch events/tutors:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // reset page when search changes
  useEffect(() => setCurrentPage(1), [search]);

  // filtered and paginated events
  const filteredEvents = events.filter((ev) => {
    const cat = ev.category || (ev.categories && ev.categories.length ? ev.categories[0] : ev.level) || '';
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (ev.title || '').toLowerCase().includes(q) ||
      (ev.tutor || '').toLowerCase().includes(q) ||
      (cat || '').toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / PAGE_SIZE));
  const displayedEvents = filteredEvents.slice((currentPage - 1) * PAGE_SIZE, (currentPage - 1) * PAGE_SIZE + PAGE_SIZE);

  const statusColors: Record<string, string> = {
    upcoming: 'bg-emerald-50 text-emerald-700',
    full: 'bg-amber-50 text-amber-600',
    completed: 'bg-slate-100 text-slate-500',
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API_BASE}/api/events/${editingId}` : `${API_BASE}/api/events`;

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...form,
          spots: Number(form.spots),
          duration: Number(form.duration),
          category: form.category === 'Other' && form.customCategory ? form.customCategory : form.category,
          status: 'upcoming'
        })
      });

      if (res.ok) {
        const savedEv = await res.json();
        if (editingId) {
          setEvents(events.map(ev => ev._id === editingId ? savedEv : ev));
        } else {
          setEvents([savedEv, ...events]);
        }
        addToast(editingId ? 'Event updated succeessfully' : 'Event created successfully!');
        // clear edit state and hide the add/edit panel
        setEditingId(null);
        setForm({ title: '', tutor: '', date: '', time: '', duration: '', spots: '', category: '', customCategory: '' });
        setShowAdd(false);
        // prevent the URL-driven opener from immediately re-opening the panel
        skipOpenRef.current = true;
        // remove any ?edit=<id> param from the URL so the panel doesn't reopen
        try {
          const params = new URLSearchParams(location.search);
          params.delete('edit');
          const newSearch = params.toString();
          navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
        } catch (e) {
          try {
            navigate(location.pathname, { replace: true });
          } catch (err) {
            // ignore navigation errors
          }
        }
      }
    } catch (error) {
      console.error('Failed to save event:', error);
    }
  };

  const performDelete = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/events/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setEvents(events.filter(ev => ev._id !== id));
        addToast('Event deleted successfully');
      }
    } catch (error) {
      console.error('Failed to delete event:', error);
      addToast('Failed to delete event', 'error');
    }
  };

  const handleDelete = (id: string) => {
    setConfirmPayload({ id, message: 'Are you sure you want to delete this event?' });
    setConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (!confirmPayload) return;
    performDelete(confirmPayload.id);
    setConfirmOpen(false);
    setConfirmPayload(null);
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setConfirmPayload(null);
  };

  const startEdit = (ev: any) => {
    setEditingId(ev._id);
    setForm({
      title: ev.title,
      tutor: ev.tutor,
      date: ev.date ? ev.date.split('T')[0] : '',
      time: ev.time,
      duration: ev.duration?.toString() || '',
      spots: ev.spots.toString(),
      category: ev.category || (Array.isArray(ev.categories) && ev.categories.length ? ev.categories[0] : (ev.categories || ev.level || '')),
      customCategory: ev.category && !['GCSE', 'A/L', 'Other'].includes(ev.category) ? ev.category : ''
    });
    setShowAdd(true);
    // push id into URL so state is shareable
    try {
      const params = new URLSearchParams(location.search);
      params.set('edit', ev._id);
      navigate(`${location.pathname}?${params.toString()}`, { replace: false });
    } catch (e) {
      // ignore
    }
  };

  // If URL contains ?edit=<id>, open edit panel accordingly
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get('edit');
    if (id) {
      // If we've just programmatically closed the editor after saving, skip auto-opening once
      if (skipOpenRef.current) {
        skipOpenRef.current = false;
        return;
      }
      setEditingId(id);
      setShowAdd(true);
      // populate form if event already loaded
      const ev = events.find((x) => x._id === id);
      if (ev) {
        setForm({
          title: ev.title,
          tutor: ev.tutor,
          date: ev.date ? ev.date.split('T')[0] : '',
          time: ev.time,
          duration: ev.duration?.toString() || '',
          spots: ev.spots?.toString() || '',
          category: ev.category || (Array.isArray(ev.categories) && ev.categories.length ? ev.categories[0] : (ev.categories || ev.level || '')),
          customCategory: ev.category && !['GCSE', 'A/L', 'Other'].includes(ev.category) ? ev.category : ''
        });
      } else {
        // leave form empty; it will populate once events load
      }
    }
  }, [location.search, events]);

  const openView = (ev: any) => {
    setViewPayload(ev);
    setViewOpen(true);
  };

  const closeView = () => {
    setViewPayload(null);
    setViewOpen(false);
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-400">Loading events...</div>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: "'Inter', sans-serif" }}>Events Management</h2>
          <p className="text-slate-500 text-sm">Schedule and manage live revision sessions</p>
        </div>
        <button onClick={() => { setShowAdd(!showAdd); setEditingId(null); setForm({ title: '', tutor: '', date: '', time: '', duration: '', spots: '', category: '', customCategory: '' }); }} className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line"></i> New Event
        </button>
      </div>

      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
        {toasts.map((t) => (
          <div key={t.id} className={`min-w-[200px] px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${t.type === 'error' ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-emerald-50 border border-emerald-200 text-emerald-700'}`}>
            <i className="ri-check-line" /> {t.message}
          </div>
        ))}
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border border-emerald-200 p-5">
          <h3 className="font-bold text-slate-800 text-sm mb-4">{editingId ? 'Edit Event' : 'Create New Event'}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Session Title</label>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. GCSE AQA Paper 1 Revision" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 bg-white">
                <option value="">Select category</option>
                <option value="GCSE">GCSE</option>
                <option value="A/L">A/L</option>
                <option value="Other">Other</option>
              </select>
              {form.category === 'Other' && (
                <input value={form.customCategory} onChange={(e) => setForm({ ...form, customCategory: e.target.value })} placeholder="Specify category here" className="mt-2 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Assign Tutor</label>
              <select required value={form.tutor} onChange={(e) => setForm({ ...form, tutor: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500 bg-white">
                <option value="">Select tutor</option>
                {tutors.map((t) => (
                  <option key={t._id} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Max Spots</label>
              <input required type="number" value={form.spots} onChange={(e) => setForm({ ...form, spots: e.target.value })} placeholder="30" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
              <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
              <input required type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Duration (Min)</label>
              <input required type="number" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="60" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div className="col-span-2 flex gap-3 justify-end">
              <button type="button" onClick={() => { setShowAdd(false); setEditingId(null); navigate(location.pathname, { replace: true }); }} className="text-sm text-slate-500 hover:text-slate-700 px-4 py-2 cursor-pointer whitespace-nowrap">Cancel</button>
              <button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap">{editingId ? 'Update Event' : 'Create Event'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Confirm modal */}
      {confirmOpen && confirmPayload && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-[90%] max-w-md">
            <h3 className="text-lg font-semibold mb-2">Confirm delete</h3>
            <p className="text-sm text-slate-600 mb-4">{confirmPayload.message}</p>
            <div className="flex justify-end gap-3">
              <button onClick={cancelDelete} className="px-4 py-2 rounded bg-slate-100 text-slate-700">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 rounded bg-red-600 text-white">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* View modal */}
      {viewOpen && viewPayload && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-2xl">
            <div className="mb-4">
              <h3 className="text-2xl font-bold text-slate-900">{viewPayload.title}</h3>
              <p className="text-sm text-slate-500 mt-1">Session details — review or take action below.</p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm text-slate-700 mt-4">
              <div>
                <div className="text-xs text-slate-500 font-semibold">Tutor</div>
                <div className="mt-1 text-slate-800">{viewPayload.tutor}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-semibold">Date</div>
                <div className="mt-1 text-slate-800">{viewPayload.date ? new Date(viewPayload.date).toLocaleDateString() : '-'}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-semibold">Time</div>
                <div className="mt-1 text-slate-800">{viewPayload.time}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-semibold">Duration</div>
                <div className="mt-1 text-slate-800">{viewPayload.duration || '60'} min</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-semibold">Spots</div>
                <div className="mt-1 text-slate-800">{viewPayload.spots}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 font-semibold">Booked</div>
                <div className="mt-1 text-slate-800">{viewPayload.booked}</div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-slate-500 font-semibold">Status</div>
                <div className="mt-1">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColors[viewPayload.status] || statusColors.upcoming}`}>{viewPayload.status || '-'}</span>
                </div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-slate-500 font-semibold">Category</div>
                <div className="mt-1 text-slate-800">{viewPayload.category || (viewPayload.categories && viewPayload.categories.length ? viewPayload.categories[0] : (viewPayload.level || '-'))}</div>
              </div>
            </div>
            <div className="mt-6 text-right">
              <button onClick={closeView} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200">Close</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-100 p-4 overflow-x-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, tutor or category" className="w-full md:w-64 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            <button type="button" onClick={() => setSearch('')} className="text-sm text-slate-500 hover:text-slate-700 px-2 py-1">Clear</button>
          </div>
        </div>

        {filteredEvents.length === 0 ? (
          <div className="text-center py-12 text-slate-400 bg-white rounded-xl border border-dashed border-slate-200">No events found.</div>
        ) : (
          <table className="min-w-full table-auto text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Tutor</th>
                <th className="px-4 py-3">Time</th>
                <th className="px-4 py-3">Spots</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedEvents.map((ev) => (
                <tr key={ev._id} className="border-t">
                  <td className="px-4 py-3 align-top w-[110px]">{formatDate(ev.date)}</td>
                  <td className="px-4 py-3 font-semibold align-top">{ev.title}</td>
                  <td className="px-4 py-3 align-top">{ev.category || (ev.categories && ev.categories.length ? ev.categories[0] : (ev.level || '-'))}</td>
                  <td className="px-4 py-3 align-top">{ev.tutor}</td>
                  <td className="px-4 py-3 align-top">{formatTime(ev.time)}</td>
                  <td className="px-4 py-3 align-top">{ev.booked}/{ev.spots}</td>
                  <td className="px-4 py-3 align-top text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openView(ev)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all cursor-pointer" title="View Session">
                        <i className="ri-eye-line"></i>
                      </button>
                      <button onClick={() => startEdit(ev)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all cursor-pointer" title="Edit Session">
                        <i className="ri-edit-line"></i>
                      </button>
                      <button onClick={() => handleDelete(ev._id)} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer" title="Delete Session">
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* pagination controls */}
        {filteredEvents.length > 0 && (
          <div className="mt-3 flex items-center justify-between">
            <div className="text-xs text-slate-500">Showing {(currentPage - 1) * PAGE_SIZE + 1} - {Math.min(currentPage * PAGE_SIZE, filteredEvents.length)} of {filteredEvents.length}</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 rounded bg-slate-100 disabled:opacity-50">Prev</button>
              <div className="text-sm">{currentPage}/{totalPages}</div>
              <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 rounded bg-slate-100 disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
